import { doc, type Firestore, type Transaction } from 'firebase/firestore';
import type { Accommodation, Expense, TripConfig } from '../types';
import { subcatsOf } from '../constants/taxonomy';
import { mergeBookingChange, stripUndef } from './dayWrites';
import { prepareBookingExpense, type BookingCost } from './bookingExpenses';
import { expenseBaseCurrency } from './expenseUtils';
import { secondaryChargeRefs } from './expenseLinks';
import { assertLinkedCostUnchanged } from './bookingCostReview';
import { claimFullCost } from './fullCostProtocol';

const metadata = (a: Accommodation) => ({
  desc: a.name, cat: subcatsOf('accommodation').find(c => c.name === a.category)?.emoji || '🏨',
  catName: a.category || 'Accommodation', sourceType: 'accommodation',
});
export function prepareAccommodationExpense(trip: TripConfig, next: Accommodation & BookingCost, userId: string,
  rateFor: (from: string, to: string, date?: string) => Promise<number>, fallbackDate?: string) {
  return prepareBookingExpense(trip, next, userId, { ...metadata(next), date: next.checkIn.slice(0, 10) || fallbackDate || '' }, rateFor);
}

/** One hotel and its expense metadata, never a stale whole-list replacement. */
export async function writeAccommodationBooking(transaction: Transaction, db: Firestore, input: {
  tripId: string; previous: Accommodation | null; next: (Accommodation & BookingCost) | null; userId: string; expense?: Expense; date?: string;
}): Promise<Accommodation | null> {
  const { tripId, previous, userId, expense } = input;
  const ref = doc(db, 'trips', tripId);
  const snapshot = await transaction.get(ref);
  if (!snapshot.exists()) throw new Error('This trip is no longer available.');
  const trip = snapshot.data() as TripConfig;
  // Legacy day-level hotels remain where they were stored; do not migrate or
  // propagate them to siblings while editing their expense link.
  const dayRef = input.date ? doc(ref, 'itinerary', input.date) : null;
  const daySnapshot = dayRef ? await transaction.get(dayRef) : null;
  const legacyHotel = daySnapshot?.data()?.accommodation as Accommodation | undefined;
  const stored = dayRef ? legacyHotel?.name ? [legacyHotel] : [] : trip.accommodations || [];
  const expenseId = previous?.expenseLogId || expense?.id || input.next?.expenseLogId;
  const expenseRef = expenseId ? doc(ref, 'expenses', expenseId) : null;
  const expenseSnapshot = expenseRef ? await transaction.get(expenseRef) : null;
  const existing = expenseSnapshot?.exists() ? expenseSnapshot.data() as Expense : null;
  assertLinkedCostUnchanged(existing, previous, input.next);
  const legacyLink = previous && !previous.id && previous.expenseLogId === expenseId;
  if (existing && ((existing.sourceType && existing.sourceType !== 'accommodation')
    || (!legacyLink && existing.sourceId && existing.sourceId !== previous?.id && existing.sourceId !== input.next?.id)
    || (!previous?.expenseLogId && (existing.sourceType !== 'accommodation' || existing.sourceId !== input.next?.id)))) {
    throw new Error('This expense is linked to a different booking. Reopen the accommodation to review it.');
  }
  if (existing?.itineraryDeleted && !previous) throw new Error('This booking was removed. Reopen the itinerary before adding it again.');
  const next = input.next && { ...input.next };
  if (next) {
    if (existing || expense) next.expenseLogId = expenseId;
    else {
      if (previous?.expenseLogId && next._logToExpenses) throw new Error('The linked expense was removed. Reopen this accommodation before logging a new cost.');
      delete next.expenseLogId;
    }
  }
  if (expense && !existing) {
    const current = stored.find(a => a.id === next?.id);
    if (expense.amountBasis === 'full-cost' && current
      && (await secondaryChargeRefs(transaction, db, tripId, current, 'accommodation')).length) {
      throw new Error('This booking already has linked expenses. Review those charges before logging its full cost.');
    }
    const active = (trip.travellers || []).filter(t => t.inviteStatus !== 'pending').map(t => t.id);
    if (stored.some(a => a.id === next?.id && a.expenseLogId)
      || previous?.expenseLogId || !next || expense.sourceId !== next.id || expense.sourceType !== 'accommodation'
      || !active.includes(expense.paid_by) || !expense.split.length || expense.split.some(id => !active.includes(id))
      || expense.targetCurrency !== expenseBaseCurrency(trip)) {
      throw new Error('The trip or expense link changed. Reopen this accommodation before saving.');
    }
  }
  const secondaryRefs = !input.next && previous
    ? await secondaryChargeRefs(transaction, db, tripId, previous, 'accommodation') : [];
  const { items, saved } = mergeBookingChange(stored, previous, next);
  if (saved?.expenseLogId !== next?.expenseLogId) throw new Error('The linked expense changed on another device. Reopen this accommodation.');
  if (dayRef) {
    if (items.length > 1 || (!saved && items.length)) throw new Error('This day has a different accommodation. Reopen it before saving.');
    transaction.update(dayRef, { accommodation: saved || {} });
  } else transaction.update(ref, { accommodations: items });
  if (expenseRef && existing) {
    if (!next) transaction.update(expenseRef, { itineraryDeleted: true, itineraryDeletedBy: userId, itineraryDeletedAt: new Date().toISOString() });
    else if (previous) transaction.update(expenseRef, {
      ...metadata(saved!), sourceId: saved!.id, ...(existing.itineraryDeleted ? { itineraryDeleted: false } : {}),
    });
  } else if (expenseRef && expense && saved) transaction.set(expenseRef, claimFullCost(transaction, db, tripId, stripUndef({ ...expense, ...metadata(saved), sourceId: saved.id,
    ...(input.date ? { importedItemDate: input.date } : {}),
  })));
  for (const linkedRef of secondaryRefs) transaction.update(linkedRef, {
    itineraryDeleted: true, itineraryDeletedBy: userId, itineraryDeletedAt: new Date().toISOString(),
  });
  return saved;
}
